/* eslint-disable graphql/template-strings */
import { useEffect, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import CourseMembership from 'bundles/accomplishments/components/s12n-certificates/CourseMembership.graphql';
import CourseVerifyCodeQuery from 'bundles/accomplishments/components/s12n-certificates/CourseVerifyCodeQuery.graphql';
import OnDemandSpecializationMemberships from 'bundles/accomplishments/components/s12n-certificates/OnDemandSpecializationMemberships.graphql';
import type {
  CourseMembershipsQueryQuery,
  CourseMembershipsQueryQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/CourseMembership';
import type {
  CourseVerifyCodeQueryQuery,
  CourseVerifyCodeQueryQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/CourseVerifyCodeQuery';
import type {
  OnDemandSpecializationMembershipsQuery,
  OnDemandSpecializationMembershipsQueryVariables,
} from 'bundles/accomplishments/components/s12n-certificates/__generated__/OnDemandSpecializationMemberships';
import type {
  S12nCertificate,
  S12nCourseCertificate,
} from 'bundles/accomplishments/components/s12n-certificates/types';
import { gradePercentageValue } from 'bundles/course-v2/utils/gradePercentageValue';

// NOTE: This will be replaced with proper graphql queries as they are migrated
export const SpecializationCertificateQuery = gql`
  query S12nCertificateQuery($code: String!) {
    OnDemandSpecializationMembershipsV1Resource(code: $code)
      @rest(
        type: "OnDemandSpecializationMembershipsV1Resource"
        path: "onDemandSpecializationMemberships.v1/?q=byCode&code={args.code}&fields=signatureTrackProfile&includes=signatureTrackProfile"
        method: "GET"
      ) {
      elements @type(name: "AccomplishmentsS12nMembership") {
        id
        userId
        s12nId
      }
      linked @type(name: "AccomplishmentsS12nLinkedData") {
        signatureTrackProfilesV1 @type(name: "AccomplishmentsSignatureTrackProfile") {
          firstName
          lastName
          middleName
        }
      }
    }
  }
`;

type OnDemandSpecializationMembershipsResponse = {
  userId: number;
  s12nId: string;
};

type SignatureTrackProfilesResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
};

type CertificateDataResponse = {
  OnDemandSpecializationMembershipsV1Resource: {
    elements: OnDemandSpecializationMembershipsResponse[];
    linked: {
      signatureTrackProfilesV1: SignatureTrackProfilesResponse[];
    };
  };
};
type CertificateVariables = {
  code: string;
};

const formatCourseCertificate = (
  coursesData: CourseMembershipsQueryQuery[],
  coursesCodesData: CourseVerifyCodeQueryQuery,
  courseId: string,
  userName: string
): S12nCourseCertificate => {
  const courseData = coursesData.find(
    (course) => course.NaptimeQueries.MembershipsV1Resource.byCode?.elements?.[0]?.id.split('~')[1] === courseId
  )?.NaptimeQueries.MembershipsV1Resource.byCode?.elements?.[0];

  const courseCodeData = coursesCodesData.NaptimeQueries.CertificateV1Resource.multiGet?.elements.find(
    (course) => course?.id.split('~')[2] === courseId
  );

  return {
    courseId,
    courseName: courseData?.course?.name ?? '',
    courseWorkload: courseData?.course?.workload ?? '',
    userName,
    partnerLogo: courseData?.course?.partnerLogo ?? '',
    partnerNames: courseData?.course?.partners?.elements.filter((p) => !!p).map((partner) => partner.name) ?? [],
    instructorNames:
      courseData?.course?.instructors?.elements.filter((i) => !!i).map((instructor) => instructor.fullName ?? '') ?? [],
    certificateCode: courseCodeData?.verifyCode ?? '',
    certificateGrade: gradePercentageValue({ grade: courseData?.grade?.score }),
    certificateGrantDate: courseCodeData?.grantedAt ?? 0,
  };
};

const formatResponse = (
  certificateCode: string,
  s12nDataResponse: OnDemandSpecializationMembershipsQuery,
  userData: CertificateDataResponse,
  coursesData: CourseMembershipsQueryQuery[],
  coursesCodesData: CourseVerifyCodeQueryQuery
): S12nCertificate => {
  const s12nData = s12nDataResponse.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements[0];
  const userName = [
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].firstName,
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].middleName,
    userData.OnDemandSpecializationMembershipsV1Resource.linked.signatureTrackProfilesV1[0].lastName,
  ]
    .join(' ')
    .replace(/\s+/, ' ');

  const courseCertificates =
    s12nData?.s12n?.courseIds
      .filter((id) => !!id)
      .map((courseId) => formatCourseCertificate(coursesData, coursesCodesData, courseId!, userName)) ?? [];

  return {
    userId: s12nData?.userId ?? 0,
    userName,
    specializationId: s12nData?.s12nId ?? '',
    specializationSlug: s12nData?.s12n?.slug ?? '',
    specializationName: s12nData?.s12n?.name ?? '',
    specializationFAQs:
      s12nData?.s12n?.metadata.additionalFaqs.map((faq) => ({
        answer: faq?.answer ?? '',
        question: faq?.question ?? '',
      })) ?? [],
    partnerNames: s12nData?.s12n?.partners?.elements.filter((p) => !!p).map((partner) => partner.name) ?? [],
    primaryCourseWorkload: courseCertificates[0].courseWorkload,
    certificateCode,
    certificateCompletionDate: s12nData?.lastModified || 0,
    certificateDescription: s12nData?.s12n?.metadata.certificateDescription ?? '',
    courseCertificates,
  };
};

const useFetchCourseDetailsData = (courseCodes: string[]) => {
  const [queryCourse] = useLazyQuery<CourseMembershipsQueryQuery, CourseMembershipsQueryQueryVariables>(
    CourseMembership,
    {
      context: { clientName: 'gatewayGql' },
    }
  );
  const [data, setData] = useState<CourseMembershipsQueryQuery[]>([]);
  const [error, setError] = useState<ApolloError | undefined>();

  const courseCodesHash = courseCodes.join('-');

  const loading = courseCodesHash.length && !data.length && !error;

  useEffect(() => {
    if (courseCodesHash.length > 0) {
      Promise.all(courseCodes.map((code) => queryCourse({ variables: { code } }))).then((results) => {
        const coursesData = results.map((result) => result.data).filter((d) => !!d);
        setData(coursesData);
        setError(results.find((result) => result.error)?.error);
      });
    }
    // we need to disable this because it asks me to add courseCodes, which, if added, creates infinte loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCodesHash]);

  return {
    data,
    error,
    loading,
  };
};

type S12nCertificateData = {
  data: S12nCertificate | null;
  loading: boolean;
  error?: ApolloError;
};

const useS12nCertificateData = (certificateCode: string): S12nCertificateData => {
  const {
    data: s12nData,
    loading: s12nLoading,
    error: s12nError,
  } = useQuery<OnDemandSpecializationMembershipsQuery, OnDemandSpecializationMembershipsQueryVariables>(
    OnDemandSpecializationMemberships,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        code: certificateCode,
      },
      skip: !certificateCode,
    }
  );

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<CertificateDataResponse, CertificateVariables>(SpecializationCertificateQuery, {
    variables: { code: certificateCode },
    skip: !certificateCode,
  });

  const userId =
    s12nData?.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements?.[0]?.userId ?? 0;

  const courseIds =
    s12nData?.NaptimeQueries.OnDemandSpecializationMembershipsV1Resource.byCode?.elements?.[0]?.s12n?.courseIds ?? [];

  const {
    data: coursesCodesData,
    loading: coursesCodesLoading,
    error: coursesCodesError,
  } = useQuery<CourseVerifyCodeQueryQuery, CourseVerifyCodeQueryQueryVariables>(CourseVerifyCodeQuery, {
    context: { clientName: 'gatewayGql' },
    variables: {
      ids: courseIds.map((courseId) => `${userId}~Course!~${courseId}`),
    },
    skip: !userId || !courseIds.length,
  });

  const courses = coursesCodesData?.NaptimeQueries.CertificateV1Resource.multiGet?.elements ?? [];

  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
  } = useFetchCourseDetailsData(courses.map((c) => c?.verifyCode ?? ''));

  const loading = s12nLoading || userLoading || coursesLoading || coursesCodesLoading;
  const error = s12nError || userError || coursesError || coursesCodesError;

  if (!loading && !error && s12nData && userData && coursesData.length === courses.length && coursesCodesData) {
    const data = formatResponse(certificateCode, s12nData, userData, coursesData, coursesCodesData);
    return { data, loading: false };
  }
  return {
    data: null,
    loading,
    error,
  };
};

export { useS12nCertificateData };
